<template>
  <ScreenWidthProvider
    v-slot="{ isWider, isNarrower }"
    class="trip-departures-calendar relative z-0 flex flex-col"
    :class="{ 'pointer-events-none opacity-50': disabled }"
  >
    <div
      v-if="isNarrower(multiCalendarBreakpoint) && overrideAirportName"
      class="flex justify-center mt-5 mb-3"
    >
      <DeparturesCalendarOverrideAirportWarning
        :override-airport-name="overrideAirportName"
      />
    </div>

    <DeparturesCalendarMonthSlider
      v-if="departures.length"
      class="mt-8 sm:mx-0"
      :class="{ 'mb-5': !calendarLoaded }"
      :departures="departures"
      :loading="!calendarLoaded || loading"
      :selected-year-month="selectedYearMonth"
      @select-year-month="onSelectYearMonth"
    />

    <div class="pl-1 -mr-4 min-[641px]:px-0 min-[641px]:mr-0">
      <table
        v-if="loading"
        class="table w-full mt-4"
      >
        <tbody>
          <tr
            class="!border-none"
          >
            <th
              v-for="i in 7"
              :key="`th-${i}`"
              class="!border-none"
            >
              <b-skeleton />
            </th>
          </tr>
          <tr
            v-for="i in 4"
            :key="`tr-${i}`"
            class="!border-none"
          >
            <td
              v-for="x in 7"
              :key="`td-${i}-${x}`"
              class="!border-none"
            >
              <b-skeleton height="5.5rem" />
            </td>
          </tr>
        </tbody>
      </table>

      <template v-else>
        <div
          v-if="isWider(multiCalendarBreakpoint) && calendarLoaded && overrideAirportName"
          class="trip-departures-calendar__header-height z-[10] -mb-10 w-full h-full absolute flex items-center justify-center"
        >
          <DeparturesCalendarOverrideAirportWarning
            :override-airport-name="overrideAirportName"
          />
        </div>

        <Calendar
          v-show="!loading"
          v-bind="calendarConfig"
          ref="vcalendar"
          expanded
          class="!bg-transparent"
          :rows="1"
          :columns="isWider(multiCalendarBreakpoint) ? 2 : 1"
          :initial-page="initialCalendarPage"
          @mouseleave="onHoverDate(null)"
          @loaded="calendarLoaded = true"
          @did-move="setCalendarActiveDateQueryParam"
        >
          <template #day-content="{ day, dayProps, dayEvents }">
            <component
              :is="isDateDirektBookLink(day.id) ? NuxtLink : 'button'"
              v-click-outside="{ fn: () => hoveringDate === day.id ? onHoverDate(null) : null }"
              class="@container !w-full p-0.5 relative text-black"
              :class="{ 'pointer-events-none': day.isDisabled }"
              v-bind="{ ...dayProps }"
              :to="isDateDirektBookLink(day.id) ? createBookLink(getDateDeparture(day.id)) : undefined"
              v-on="dayEvents"
              @click="() => getDateDeparture(day.id)?.bookable !== 0
                ? onHoverDate(day.id)
                : $emit('openWaitinglist', getDateDeparture(day.id))
              "
              @mouseenter="onHoverDate(day.id)"
              @mouseleave="onHoverDate(null)"
            >
              <div
                class="transition-bg border rounded-lg py-1 w-full flex flex-col items-start px-1 trip-departures-calendar__date h-full"
                style="min-height:100cqw"
                :class="dayClass(day.id)"
              >
                <strong
                  class="trip-departures-calendar__date-day-number mb-2 text-sm/3 @[85px]:text-base/3"
                  :class="getDateDeparture(day.id) && getDateDeparture(day.id)?.bookable !== 0 ? 'text-blue-800' : 'text-black'"
                >
                  {{ day.day }}
                </strong>

                <div
                  v-if="getDateDeparture(day.id)"
                  class="flex flex-col items-start text-left mt-auto"
                >
                  <template v-if="getDateDeparture(day.id).bookable !== 0">
                    <small class="font-secondary text-[.65rem]/3 @[65px]:text-sm/3 @[85px]:text-base/3 mb-1">
                      {{ getDateDeparturesDaysText(day.id) }}
                    </small>
                    <strong
                      v-if="getDateDeparture(day.id).discounted"
                      class="text-nowrap font-normal strikethrough -mx-0.5 font-secondary text-[.65rem]/3 @[65px]:text-sm/3 @[85px]:mt-0 @[85px]:text-base/3 mb-1"
                    >
                      {{ $n(getDateDeparture(day.id).original_price) }}
                    </strong>
                    <span
                      class="font-bold text-xs/3 @[65px]:text-base/3 !leading-3.5"
                      :class="{ 'text-discounted': getDateDeparture(day.id).discounted }"
                    >
                      {{ $n(getDateDeparture(day.id).price) }}
                    </span>
                  </template>

                  <small
                    v-else
                    data-i18n="waitingList"
                    class="font-secondary leading-3"
                  >
                    {{ $t('waitingList') }}
                  </small>
                </div>
              </div>

              <Transition name="quick-fade">
                <div
                  v-if="hoveringDate === day.id && datesDepartures[day.id]?.length > 1"
                  class="trip-departures-calendar__date-multiple-options absolute bg-white z-10 rounded-lg shadow-blur -m-1 p-2 top-0 font-secondary"
                >
                  <div class="flex flex-col p-1 items-start -mb-2">
                    <span
                      data-i18n="trip.departuresCalendar.differentLengths"
                      class="whitespace-pre-line text-nowrap text-left text-lg/6 mb-2"
                    >
                      {{ toUnixNewlines($t('trip.departuresCalendar.differentLengths')) }}
                    </span>

                    <div class="flex flex-nowrap">
                      <nuxt-link
                        v-for="(departure, index) in datesDepartures[day.id]"
                        :key="index"
                        class="btn btn-blue-800 lowercase rounded-2xl px-2 py-1 text-sm mb-2 mr-2 text-nowrap"
                        :to="createBookLink(departure)"
                        @mouseenter="onHoverDate(day.id, index)"
                      >
                        {{ departure.days }} {{ $t('days', departure.days) }}
                      </nuxt-link>
                    </div>
                  </div>
                </div>
              </Transition>
            </component>
          </template>
        </Calendar>
      </template>
    </div>
  </ScreenWidthProvider>
</template>

<script setup>
import { SERVER_DATE_FORMAT } from '@packages/date/runtime/constants/formats.ts'

import { NuxtLink } from '#components'

const props = defineProps({
  trips: {
    type: Array,
    default: () => [],
  },

  overrideAirportName: {
    type: String,
    default: null,
  },

  multiCalendarBreakpoint: {
    type: Number,
    default: getTailwindBreakpointsPx().xl,
  },

  loading: {
    type: Boolean,
    default: false,
  },

  disabled: {
    type: Boolean,
    default: false,
  },
})

defineEmits(['openWaitinglist'])

const { urls: localeURLs } = useLocale()
const dayjs = useDayjs()
const route = useRoute()
const router = useRouter()

const hoveringDate = ref(null)
const lastHoveringDate = ref(null)
const highlightedDates = ref([])
const calendarLoaded = ref(false)

const selectedYearMonth = ref(null)

const vcalendar = useTemplateRef('vcalendar')

const departuresInterval = computed(() => {
  const dateSortedDepartures = [...props.trips]
    .sort((a, b) => a.date > b.date ? 1 : -1)

  const first = dateSortedDepartures[0]
  const last = dateSortedDepartures.pop()

  return {
    first: first
      ? dayjs(first.date).toDate()
      : null,
    last: last
      ? dayjs(last.date).toDate()
      : null,
  }
})

const calendarConfig = computed(() => ({
  minDate: departuresInterval.value.first,
  maxDate: departuresInterval.value.last,
  borderless: true,
  locale: {
    masks: {
      weekdays: 'WWWW',
    },
  },
}))

const datesDepartures = computed(() =>
  [...props.trips]
    .sort((a, b) => {
      if (b.bookable === 0) {
        return 1
      }

      return ((b.bookable === 0 && a.bookable !== 0) || b.price > a.price) ? 1 : -1
    })
    .reduce((dateGroupedTrips, trip) => ({
      ...dateGroupedTrips,
      [trip.date]: dateGroupedTrips[trip.date] && dateGroupedTrips[trip.date].every(({ days }) => trip.days !== days)
        ? [
          trip,
          ...dateGroupedTrips[trip.date],
        ]
        : [trip],
    }), {})
)

const departures = computed(() => Object.values(datesDepartures.value))

const initialCalendarPage = computed(() => {
  if (!route.query.calendar_date) {
    return null
  }

  const [year, month] = route.query.calendar_date.split('-')

  return {
    year,
    month,
  }
})

const isDateDirektBookLink = (date) => {
  return getDateDeparture(date)?.bookable !== 0 && datesDepartures.value[date]?.length === 1
}

const createBookLink = (departure) => {
  return `${localeURLs.book}/${departure.tripid}?price=${departure.price}${departure.single_price ? '&singlePrice=' + departure.single_price : ''}`
}

const onHoverDate = (date, dateDepartureIndex) => {
  if (!date) {
    highlightedDates.value = []
    hoveringDate.value = null
  }

  hoveringDate.value = date

  const dateDepartures = datesDepartures.value[date]
  if (!dateDepartures) {
    return
  }

  if (dateDepartures.length > 1 && dateDepartureIndex === undefined) {
    return
  }

  const dateDeparture = dateDepartures[dateDepartureIndex ?? 0]

  if (dateDeparture.bookable === 0) {
    return
  }

  highlightedDates.value = new Array(dateDeparture.days)
    .fill(null)
    .map((_, index) => dayjs(date).add(index, 'day').format(SERVER_DATE_FORMAT))
}

const getDateDeparture = (date) => {
  return datesDepartures.value[date]?.[0]
}

const dayClass = (date) => {
  const dateDeparture = getDateDeparture(date)

  const isSelected = date === hoveringDate.value

  // Highlighted date
  if (!isSelected && highlightedDates.value.includes(date)) {
    return 'bg-blue-800 text-white trip-departures-calendar__date-highlight !border-0 px-1.5 py-1.5'
  }

  // Disabled date
  if (!dateDeparture) {
    return 'bg-gray-400 border border-gray-600'
  }

  if (dateDeparture.bookable === 0) {
    return 'bg-white border-0'
  }

  const classes = []

  const hasMultiOptions = datesDepartures.value[date].length > 1

  if (isSelected) {
    if (hasMultiOptions) {
      // Show multiple options
      classes.push('z-20 border-blue-800')
    } else {
      // Active
      classes.push('border-blue-800 bg-white border-2')
    }
  } else {
    classes.push('border-gray-600 bg-white')
  }

  if (hasMultiOptions && date === lastHoveringDate.value) {
    classes.push('z-20')
  }

  return classes
}

const getDateDeparturesDaysText = (date) => {
  return [
    ...new Set(datesDepartures.value[date].map(({ days }) => days + 'd')),
  ].join(' / ')
}

const setCalendarActiveDateQueryParam = (event) => {
  const movedMonthId = event.id

  if (movedMonthId) {
    router.replace({
      query: {
        ...route.query,
        calendar_date: movedMonthId,
      },
    })
  }
}

const onSelectYearMonth = (month) => {
  selectedYearMonth.value = month

  const yearMonthDayjs = dayjs(month)

  vcalendar.value.vcalendar.move({
    month: yearMonthDayjs.month() + 1,
    year: yearMonthDayjs.year(),
  })
}

const getFirstDepartureYearMonth = () => {
  return dayjs(departuresInterval.value.first).format('YYYY-MM')
}

watch(hoveringDate, (_, prevValue) => {
  lastHoveringDate.value = prevValue
})

watch(datesDepartures, (value) => {
  if (!selectedYearMonth.value) {
    if (initialCalendarPage.value) {
      selectedYearMonth.value = `${initialCalendarPage.value.year}-${initialCalendarPage.value.month}`
    } else {
      selectedYearMonth.value = getFirstDepartureYearMonth()
    }

    return
  }

  const selectedYearMonthHasDepartures = Object.keys(value)
    .some((date) => date.startsWith(selectedYearMonth.value))

  if (!selectedYearMonthHasDepartures) {
    selectedYearMonth.value = getFirstDepartureYearMonth()
  }
}, { immediate: true })

defineExpose({
  vcalendar,
})
</script>

<style lang="scss">
$multi-calendar-header-height: 120px;
$multiple-options-padding: .5rem;

.trip-departures-calendar {
  &__date-highlight {
    .strikethrough:before {
      border-color: white;
    }

    .trip-departures-calendar__date {
      &-day-number {
        color: white !important;
      }
    }
  }

  &__date-multiple-options {
    min-height: calc(100% + $multiple-options-padding);
    min-width: calc(100% + $multiple-options-padding);

    left: 0;
    padding-left: calc(100% + $multiple-options-padding);
  }

  .vc-week {
    .vc-day:nth-last-child(-n+4) {
      .trip-departures-calendar__date-multiple-options {
        left: unset;
        right: 0;
        padding-left: inherit;
        padding-right: calc(100% + $multiple-options-padding);
      }
    }
  }

  &__header-height {
    height: $multi-calendar-header-height;
  }

  /**
   * VCalendar styling
   */
  .vc {
    &-container {
      font-family: theme('fontFamily.primary');
    }

    &-pane-layout {
      @apply gap-x-10;
    }

    &-day-content {
      height: auto;

      &:hover {
        background-color: unset !important;
      }
    }

    &-day {
      display: flex;
      z-index: unset;
    }

    &-focus {
      box-shadow: none !important;
    }

    &-weekday {
      @apply mb-3 text-black font-normal font-secondary text-base;

      @media (max-width: theme('screens.sm')) {
        @apply text-xs;
      }
    }

    &-header {
      height: $multi-calendar-header-height;
      margin-top: 0;
    }

    &-pane {
      // Single calendar
      &:only-child {
        .vc-weekdays {
          @apply pt-7 pb-3;
        }

        .vc-header {
          display: none;
        }
      }
    }

    &-title {
      pointer-events: none !important;
      font-weight: 700 !important;
    }

    &-pane-header-wrapper {
      display: none;
    }
  }

  @media (max-width: 641px) {
    .vc-weekdays {
      margin-left: -20px;
    }
  }
}
</style>